/* eslint-disable no-console */
import { useChatListState } from "@/zustand/ChatListState";
import { useNavigationState } from "@/zustand/NavigationState";
import { navigateToChatList } from "@/lib/navigationRef";
import { parseP2PPeer } from "@/lib/p2pPeer";
import { isDeletedUserLabel } from "@/lib/deletedSender";
import { extractTimestampFromUUID7 } from "@/utils/uuid7";
import { decryptMessageContent } from "@/middleware/crypto";
import { tryDecrypt } from "@/middleware/decryptionError";
import { isUserAuthenticated } from "./auth";
import { getEnrichedChats, getMutedChats } from "@/server/services/chatService";
import { getUserData } from "@/server/services/userService";
import { DEFAULT_SYNC_DATE } from "@/config/constants";
import {
  addChatToLocalDB,
  getChatFromLocalDB,
  getGroupChatsFromLocalDB,
  getP2PChatsFromLocalDB,
  removeChatFromLocalDB,
  updateChatInLocalDB,
} from "@/indexedDB/services/chats";
import { removeChatDataFromLocalDB } from "@/indexedDB/services/chatTeardown";
import { createLogger } from "@/utils/debug";
import {
  ChatEntity,
  InitChatStatus,
  MessageEntity,
  MessageSendingStatus,
} from "@/indexedDB/types";
import { ChatType, GroupChatEntity, P2PChatEntity } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";
import { useUserAvatarMapState } from "@/zustand/UserAvatarMapState";

// Create module-specific logger
const log = createLogger("ChatsSync");

/**
 * Purpose: Synchronizes chats between server and local database
 *
 * How it works:
 * 1. Fetches chat list from server
 * 2. Processes and formats group and private chats
 * 3. Updates local database with new/updated chats
 * 4. Removes chats from local DB that no longer exist on server
 *
 * @param options - Pagination options
 * @param options.take - Number of chats to fetch
 * @param options.skip - Number of chats to skip
 * @returns Promise resolving to a boolean indicating success
 */

// Function to safely reset sync state
export const resetSyncState = () => {
  useChatListState.getState().setChatListSyncing(false);
  log.info("Reset both syncInProgress and chatListSyncing to false");
};

// Function to check if sync is in progress
export const isSyncInProgress = () => {
  const chatListSyncing = useChatListState.getState().chatListSyncing;
  return chatListSyncing;
};

/**
 * Process and format group chats from server response
 *
 * @param enrichedChats - Group chats data from server
 * @param userFingerPrint - Current user authentication data
 * @returns Processed group chats ready for local storage
 */
export const processGroupChats = async (
  enrichedChats: Array<GroupChatEntity>,
  userFingerPrint: { username: string },
): Promise<
  Omit<
    ChatEntity,
    "isMuted" | "lastSyncedAt" | "firstPhaseMsgListInit" | "fullMsgListInit"
  >[]
> => {
  log.info(`Starting with ${enrichedChats.length} group chats`);
  return Promise.all(
    enrichedChats.map(async (groupChat) => {
      // Process and decrypt last message
      let lastMessageEntry: MessageEntity | null = null;

      if (groupChat.chat.last_message) {
        // Empty `secure_payload` at epoch 0 is the transient missing-eCSK
        // case: skip the decrypt and persist `failed` without bumping
        // attempts. Rotated epochs (>0) resolve the CSK from the epoch cache,
        // so we still attempt without an inline secure_payload.
        const lastEpoch = groupChat.chat.last_message.key_epoch ?? 0;
        const hasKey = !!groupChat.member.secure_payload || lastEpoch > 0;
        const decrypted = hasKey
          ? await tryDecrypt(() =>
              decryptMessageContent(
                groupChat.chat.id,
                lastEpoch,
                groupChat.member.secure_payload,
                groupChat.chat.last_message!.content,
                String(groupChat.chat.last_message!.attachment_meta || ""),
              ),
            )
          : ({ ok: false } as const);

        // Persist a placeholder MessageEntity even on decrypt failure so the
        // chat-list preview shows "Decrypting…" and the retry orchestrator
        // can heal `chats.lastMessage` once keys arrive.
        lastMessageEntry = {
          _clientId:
            groupChat.chat.last_message.client_id ||
            groupChat.chat.last_message.id, // Use client_id if present, otherwise use server id
          id: groupChat.chat.last_message.id,
          chatId: groupChat.chat.id,
          sender: groupChat.chat.last_message.sender,
          ...(groupChat.chat.last_message.sender_deleted
            ? { senderDeleted: true }
            : {}),
          timestamp: extractTimestampFromUUID7(groupChat.chat.last_message.id),
          category: groupChat.chat.last_message.category,
          content: decrypted.ok ? decrypted.data : { text: "" },
          hasAttachment: !!groupChat.chat.last_message.attachment_meta,
          isEdited: groupChat.chat.last_message.is_edited,
          isOutgoing:
            userFingerPrint.username === groupChat.chat.last_message.sender,
          // thread related
          commentCount: groupChat.chat.last_message.comment_count,
          senderAvatar: groupChat.chat.last_message.sender_avatar,
          parentCommentCount: groupChat.chat.last_message.parent_comment_count,
          // comment thread
          threadId: groupChat.chat.last_message.thread_id,
          commentSentAt: null, // No longer needed with UUID7
          // reply related
          isReply: !!groupChat.chat.last_message.parent_id,
          sendingStatus: MessageSendingStatus.SENT,
          decryptionStatus: decrypted.ok ? "ok" : "failed",
          ...(decrypted.ok || !hasKey ? {} : { decryptionAttempts: 1 }),
        };
      }

      // usePinnedMessageState.getState().setPinnedMessageIdsForChat(
      //   groupChat.chat.id,
      //   groupChat.pinned_messages.map((m) => ({
      //     id: m.id,
      //     timestamp: m.sent_at,
      //   })),
      // );

      const pinnedMessageIds = groupChat.pinned_messages.map((m) => m.id);

      return {
        id: groupChat.chat.id,
        hubId: groupChat.chat.hub_id,
        eCSK: groupChat.member.secure_payload,
        currentEpoch: groupChat.chat.current_epoch ?? 0,
        name: groupChat.chat.name,
        description: groupChat.chat.description,
        allowThreads: !!groupChat.chat.threads_enabled,
        canPost:
          groupChat.chat.can_post === null
            ? MemberRole.MEMBER
            : groupChat.chat.can_post,
        type: ChatType.GROUP,
        avatar: {
          attachmentId: groupChat.chat.picture,
        },
        timestamp: new Date(groupChat.chat.timestamp).getTime(),
        memberCount: groupChat.chat.member_count,
        lastMessage: lastMessageEntry,
        // TO DO => FIX THIS ON BE
        lastMessageTimestamp: groupChat.chat.last_message
          ? extractTimestampFromUUID7(groupChat.chat.last_message.id)
          : undefined,
        unreadCount: groupChat.unread_count,
        isPinned: groupChat.is_pinned,
        isArchived: groupChat.is_archived,
        isPublic: groupChat.chat.is_searchable,
        pinnedMessageIds,
        initStatus: InitChatStatus.SUCCESS,
        roleAsMember: {
          username: groupChat.member.username,
          // Convert string role to MemberRole enum
          role: Number(groupChat.member.role) as MemberRole,
        },
      };
    }),
  ).then((result) => {
    log.info(`Processed ${result.length} group chats`);
    return result;
  });
};

/**
 * Process and format private chats from server response
 *
 * @param enrichedP2PChats - Private chats data from server
 * @param userFingerPrint - Current user authentication data
 * @returns Processed private chats ready for local storage
 */
export const processPrivateChats = async (
  enrichedP2PChats: Array<P2PChatEntity>,
  userFingerPrint: { username: string },
): Promise<
  Omit<
    ChatEntity,
    "isMuted" | "lastSyncedAt" | "firstPhaseMsgListInit" | "fullMsgListInit"
  >[]
> => {
  log.info(`Starting with ${enrichedP2PChats.length} private chats`);
  return Promise.all(
    enrichedP2PChats.map(async (privateChat) => {
      // NEVER throw and NEVER skip here: this runs inside one Promise.all
      // (a throw fails the entire chat-list sync), and a skipped chat would
      // be absent from allChats — removeDeletedChats would then DELETE the
      // DM locally. parseP2PPeer falls back to { username: raw } for the
      // Umbra plain-string shape (incl. "Deleted User").
      const parsed_user_two = parseP2PPeer(privateChat.p2p_chat.user_two) ?? {
        username: "",
        profileImage: null,
      };

      // Process and decrypt last message
      let lastMessageEntry: MessageEntity | null = null;

      if (privateChat.p2p_chat.last_message) {
        const lastEpoch = privateChat.p2p_chat.last_message.key_epoch ?? 0;
        const hasKey = !!privateChat.p2p_chat.secure_payload || lastEpoch > 0;
        const decrypted = hasKey
          ? await tryDecrypt(() =>
              decryptMessageContent(
                privateChat.p2p_chat.id,
                lastEpoch,
                privateChat.p2p_chat.secure_payload,
                privateChat.p2p_chat.last_message!.content,
                String(
                  privateChat.p2p_chat.last_message!.attachment_meta || "",
                ),
              ),
            )
          : ({ ok: false } as const);

        lastMessageEntry = {
          _clientId:
            privateChat.p2p_chat.last_message.client_id ||
            privateChat.p2p_chat.last_message.id, // Use client_id if present, otherwise use server id
          id: privateChat.p2p_chat.last_message.id,
          chatId: privateChat.p2p_chat.id,
          sender: privateChat.p2p_chat.last_message.sender,
          ...(privateChat.p2p_chat.last_message.sender_deleted
            ? { senderDeleted: true }
            : {}),
          timestamp: extractTimestampFromUUID7(
            privateChat.p2p_chat.last_message.id,
          ),
          category: privateChat.p2p_chat.last_message.category,
          content: decrypted.ok ? decrypted.data : { text: "" },
          hasAttachment: !!privateChat.p2p_chat.last_message.attachment_meta,
          isEdited: privateChat.p2p_chat.last_message.is_edited,
          isOutgoing:
            userFingerPrint.username ===
            privateChat.p2p_chat.last_message.sender,
          // thread related
          commentCount: privateChat.p2p_chat.last_message.comment_count,
          senderAvatar: privateChat.p2p_chat.last_message.sender_avatar,
          parentCommentCount:
            privateChat.p2p_chat.last_message.parent_comment_count,
          // comment thread
          threadId: privateChat.p2p_chat.last_message.thread_id,
          commentSentAt: null, // No longer needed with UUID7
          // reply related
          isReply: !!privateChat.p2p_chat.last_message.parent_id,
          sendingStatus: MessageSendingStatus.SENT,
          decryptionStatus: decrypted.ok ? "ok" : "failed",
          ...(decrypted.ok || !hasKey ? {} : { decryptionAttempts: 1 }),
        };
      }

      // usePinnedMessageState.getState().setPinnedMessageIdsForChat(
      //   privateChat.p2p_chat.id,
      //   privateChat.pinned_messages.map((m) => ({
      //     id: m.id,
      //     timestamp: m.sent_at,
      //   })),
      // );

      const pinnedMessageIds = privateChat.pinned_messages.map((m) => m.id);

      return {
        id: privateChat.p2p_chat.id,
        hubId: null,
        eCSK: privateChat.p2p_chat.secure_payload,
        currentEpoch: privateChat.p2p_chat.current_epoch ?? 0,
        name: parsed_user_two.username,
        type: ChatType.P2P,
        avatar: {
          attachmentId: parsed_user_two.profileImage ?? "",
          secondMemberId: parsed_user_two.username,
        },
        timestamp: new Date(privateChat.p2p_chat.timestamp).getTime(),
        memberCount: 2,
        lastMessage: lastMessageEntry,
        // TO DO => FIX THIS ON BE
        lastMessageTimestamp: privateChat.p2p_chat.last_message
          ? extractTimestampFromUUID7(privateChat.p2p_chat.last_message.id)
          : undefined,
        unreadCount: privateChat.unread_count,
        isPinned: privateChat.is_pinned,
        isArchived: privateChat.is_archived,
        isPublic: false,
        canPost: MemberRole.MEMBER,
        allowThreads: false,
        pinnedMessageIds,
        initStatus: InitChatStatus.SUCCESS,
        roleAsMember: {
          username: userFingerPrint.username,
          role:
            userFingerPrint.username === parsed_user_two.username
              ? MemberRole.MEMBER
              : MemberRole.OWNER,
        },
      };
    }),
  ).then((result) => {
    log.info(`Processed ${result.length} private chats`);
    return result;
  });
};

/**
 * Updates the local database with new or updated chats
 *
 * @param chats - List of chats to update in the database
 */
export const updateLocalDatabase = async (
  chats: Omit<
    ChatEntity,
    "isMuted" | "lastSyncedAt" | "firstPhaseMsgListInit" | "fullMsgListInit"
  >[],
  mutedChatIds: string[] = [],
): Promise<void> => {
  log.info(`Starting with ${chats.length} chats`);
  // Process each chat in parallel
  await Promise.all(
    chats.map(async (chat) => {
      if (!chat.id) {
        return;
      }

      const isMuted = mutedChatIds.includes(chat.id);
      const isChatInLocalDB = await getChatFromLocalDB(chat.id);

      if (isChatInLocalDB) {
        // Update existing chat, refreshing isMuted from server
        await updateChatInLocalDB(chat.id, { ...chat, isMuted });
      } else {
        // Add new chat with isMuted from server
        await addChatToLocalDB({
          ...chat,
          lastSyncedAt: DEFAULT_SYNC_DATE,
          isMuted,
          firstPhaseMsgListInit: false,
          fullMsgListInit: false,
        });
      }
    }),
  ).then(() => {
    log.info("Completed updating local database");
  });
};

/**
 * Removes chats from local database that no longer exist on the server
 *
 * @param currentChats - List of current chats from server
 */
export const removeDeletedChats = async (
  currentChats: { id: string }[],
  fetchStartedAt: string,
): Promise<void> => {
  log.info(`Starting with ${currentChats.length} current chats`);
  // Get all local chats
  log.info("Fetching local chats from DB");
  const localP2PChats = await getP2PChatsFromLocalDB();
  const localGroupChats = await getGroupChatsFromLocalDB();
  const localChats = [...localP2PChats, ...localGroupChats];
  log.info(
    `Found ${localP2PChats.length} P2P chats and ${localGroupChats.length} group chats locally`,
  );

  // Find chats that exist locally but not in the current list from server.
  // Skip any chat whose lastSyncedAt is newer than when the HTTP request
  // was initiated — those were written by a socket event that arrived while
  // the request was in-flight and must not be treated as stale.
  // A missing/null lastSyncedAt (legacy rows) is treated as the oldest
  // possible value and is therefore eligible for removal.
  const chatsToRemove = localChats.filter(
    (localChat) =>
      !currentChats.some((currentChat) => currentChat.id === localChat.id) &&
      (!localChat.lastSyncedAt || localChat.lastSyncedAt < fetchStartedAt),
  );
  log.info(`Found ${chatsToRemove.length} chats to remove`);

  // Remove each chat and its messages from local DB
  for (const chatToRemove of chatsToRemove) {
    log.info(`Processing removal of chat ${chatToRemove.id}`);

    // Check if this is the active chat — navigate away via URL (URL-first pattern)
    const activeChatId = useNavigationState.getState().activeChatId;
    if (activeChatId === chatToRemove.id) {
      log.info(`Resetting active chat ID (was ${activeChatId})`);
      navigateToChatList();
    }

    // Remove the chat's data (messages, pinned, attachments incl. OPFS
    // blobs, links, per-chat query caches), THEN the chat row — matching the
    // sweep-before-row order at every other teardown site.
    log.info(`Removing data for chat ${chatToRemove.id}`);
    await removeChatDataFromLocalDB(chatToRemove.id);
    log.info(`Removing chat ${chatToRemove.id} from local DB`);
    await removeChatFromLocalDB(chatToRemove.id);
    log.info(`Completed removal of chat ${chatToRemove.id}`);
  }
  log.info("Completed all chat removals");
};

/**
 * Seeds the UserAvatarMapState with the latest avatar data from P2P chats.
 *
 * After logout/login, the avatar map is empty. Without a baseline, stale events
 * replayed via FETCH_EVENTS can populate the map with outdated avatars.
 * This function establishes a correct baseline by using the server's current
 * profile_image for each P2P chat partner, with priority=true since this data
 * comes directly from the server and represents the ground truth.
 */
const seedAvatarMapFromChats = (
  enrichedP2PChats: Array<P2PChatEntity>,
  userFingerPrint: { username: string },
): void => {
  const avatarEntries: Array<{
    username: string;
    attachmentId: string;
  }> = [];

  for (const privateChat of enrichedP2PChats) {
    const peer = parseP2PPeer(privateChat.p2p_chat.user_two);
    // Only seed for the OTHER user in the P2P chat (not yourself). Never
    // seed/tombstone the deleted-user label itself — it is not an account,
    // and a map entry under that shared key would leak one deleted user's
    // avatar onto every deleted user.
    if (
      peer?.username &&
      peer.username !== userFingerPrint.username &&
      !isDeletedUserLabel(peer.username)
    ) {
      if (peer.profileImage) {
        avatarEntries.push({
          username: peer.username,
          attachmentId: peer.profileImage,
        });
      } else {
        // Avatar was deleted while offline — tombstone it so the stale
        // persisted entry doesn't keep winning over the empty fallback.
        useUserAvatarMapState.getState().removeUserAvatar(peer.username, true);
      }
    }
  }

  if (avatarEntries.length > 0) {
    const now = new Date().toISOString();
    for (const entry of avatarEntries) {
      useUserAvatarMapState.getState().setUserAvatar(
        entry.username,
        entry.attachmentId,
        now,
        true, // Priority: server data is the ground truth
      );
    }
    log.info(
      `Seeded avatar map with ${avatarEntries.length} P2P chat partner avatars`,
    );
  }
};

/* eslint-disable no-console */
export const syncChats = async ({
  take,
  skip,
  skipRemove,
}: {
  take: number;
  skip: number;
  skipRemove?: boolean;
}): Promise<boolean> => {
  log.info("Starting sync", { take, skip, skipRemove });

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    log.info("Auth not present, aborting");
    // throw new Error("Auth not present");
    return false;
  }

  const setChatListSyncing = useChatListState.getState().setChatListSyncing;
  // Save the active chat ID and its pinned messages before sync to restore them later
  // const activeChatId = useNavigationState.getState().activeChatId;

  // // Save the current pinned messages for the active chat to prevent flickering
  // const activeChatPinnedMessages = activeChatId
  //   ? usePinnedMessageState.getState().pinnedMessages[activeChatId] || []
  //   : [];

  // Check if already syncing or another sync is in progress using our helper
  // This prevents multiple concurrent executions and potential race conditions
  if (isSyncInProgress()) {
    log.info("Already syncing or in progress");
    return false;
  }

  setChatListSyncing(true);
  log.info("Both locks acquired, starting sync process");

  try {
    log.info("Setting syncInProgress to true");

    // Stamp the moment just before the HTTP request so that any chat written
    // by a socket event while the request is in-flight can be identified and
    // protected from the stale-snapshot deletion pass below.
    const fetchStartedAt = new Date().toISOString();

    // Get chat list from server
    log.info("Fetching chats from server");
    const chatList = await getEnrichedChats({
      chats_take: take,
      chats_skip: skip,
    });
    log.info(
      `Received ${chatList.enriched_chats?.length || 0} group chats and ${chatList.enriched_p2p_chats?.length || 0} private chats`,
    );

    // Process group and private chats and fetch muted chat IDs in parallel
    // Note: Type casting is used here as a temporary solution until API types are properly aligned
    log.info("Starting parallel processing of group and private chats");

    const [groupChats, privateChats, mutedChatIds] = await Promise.all([
      processGroupChats(chatList.enriched_chats, userFingerPrint),
      processPrivateChats(chatList.enriched_p2p_chats, userFingerPrint),
      getMutedChats(userFingerPrint.username),
    ]);

    log.info(
      `Processed ${groupChats.length} group chats and ${privateChats.length} private chats, ${mutedChatIds.length} muted`,
    );

    const allChats = [...groupChats, ...privateChats];
    log.info(`Processed ${allChats.length} total chats`);

    // Seed the UserAvatarMapState with P2P chat partner avatars from server.
    // This provides a correct baseline so that stale replayed events
    // (from FETCH_EVENTS after logout/login) cannot override with old avatars.
    // We use priority=true because this data comes directly from the server's
    // current state and represents the ground truth for user avatars.
    seedAvatarMapFromChats(chatList.enriched_p2p_chats, userFingerPrint);

    // Refresh the current user's own avatar from the server.
    // The map is persisted, so a stale self-avatar from a previous session
    // (e.g. changed on another device while offline) must be reconciled.
    try {
      const userData = await getUserData(userFingerPrint.username);
      if (userData.profile_image) {
        useUserAvatarMapState
          .getState()
          .setUserAvatar(
            userFingerPrint.username,
            userData.profile_image,
            new Date().toISOString(),
            true,
          );
      } else {
        useUserAvatarMapState
          .getState()
          .removeUserAvatar(userFingerPrint.username, true);
      }
    } catch {
      // Non-critical — avatar will resolve on next member fetch
    }

    // Update local database with new or updated chats
    log.info("Updating local database");
    await updateLocalDatabase(allChats, mutedChatIds);
    log.info("Local database update complete");

    // Clean up removed chats
    if (skipRemove) {
      log.info("Skipping cleanup of removed chats");
    } else {
      log.info("Starting cleanup of removed chats");
      await removeDeletedChats(allChats, fetchStartedAt);
      log.info("Cleanup of removed chats complete");
    }

    log.info("Completed sync successfully");

    // // Restore pinned messages and selection for the active chat after sync
    // if (activeChatId && activeChatPinnedMessages.length > 0) {
    //   log.info(`Restoring pinned messages for active chat ${activeChatId}`);

    //   // First ensure the pinned messages data is preserved
    //   const currentState = usePinnedMessageState.getState();
    //   const updatedPinnedMessages = {
    //     ...currentState.pinnedMessages,
    //   };

    //   // Only update if the active chat's pinned messages were cleared or changed
    //   const currentMessages = currentState.pinnedMessages[activeChatId] || [];
    //   if (
    //     currentMessages.length === 0 ||
    //     JSON.stringify(currentMessages) !==
    //       JSON.stringify(activeChatPinnedMessages)
    //   ) {
    //     updatedPinnedMessages[activeChatId] = activeChatPinnedMessages;

    //     // Update the store with preserved pinned messages
    //     usePinnedMessageState.setState({
    //       pinnedMessages: updatedPinnedMessages,
    //     });
    //   }

    //   // Then restore the selection state
    //   usePinnedMessageState.getState().setChat(activeChatId);
    // }

    return true;
  } catch (error) {
    // Get error details for logging
    log.error("Error syncing chats", error);
    log.info(
      `Stack trace: ${error instanceof Error ? error.stack : "No stack trace"}`,
    );

    // Reset both states
    resetSyncState();
    log.info("[syncChats] Reset sync state in catch block");
    return false;
  } finally {
    // Reset both states
    resetSyncState();
    log.info("[syncChats] Reset sync state in finally block");
  }
};
